import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  Snackbar,
  Alert,
  Chip,
} from '@mui/material';
import {
  Shield as ShieldIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { portfolioAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import MarketRegimeCard from '../components/safe-invest/MarketRegimeCard';
import SectorRotationLayer from '../components/safe-invest/SectorRotationLayer';
import StockCandidatesLayer from '../components/safe-invest/StockCandidatesLayer';
import SafeInvestSkeleton from '../components/safe-invest/SafeInvestSkeleton';

const SafeInvest = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [regimeData, setRegimeData] = useState(null);
  const [interpretation, setInterpretation] = useState(null);
  const [stocksData, setStocksData] = useState(null);
  const [sectorsData, setSectorsData] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [regimeRes, stocksRes, sectorsRes] = await Promise.allSettled([
        portfolioAPI.getSafeInvestRegime(),
        portfolioAPI.getPortfolioSummary(),
        portfolioAPI.getSafeInvestSectorRotation(),
      ]);
      if (regimeRes.status === 'fulfilled' && regimeRes.value.data.success) {
        setRegimeData(regimeRes.value.data.data);
        setInterpretation(regimeRes.value.data.interpretation);
      }
      if (stocksRes.status === 'fulfilled' && stocksRes.value.data.success) setStocksData(stocksRes.value.data);
      if (sectorsRes.status === 'fulfilled' && sectorsRes.value.data?.sectors) setSectorsData(sectorsRes.value.data.sectors);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    setLoading(true);
    try {
      await fetchData();
      setSnackbar({ open: true, message: t('safeInvest.success.dataUpdated', 'Đã cập nhật dữ liệu'), severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: t('safeInvest.errors.updateData', 'Không thể cập nhật dữ liệu'), severity: 'error' });
    }
  }, [t]);

  const getRatingLabel = (rating) => ({
    'very_strong': t('safeInvest.layer2.veryStrong'),
    'strong': t('safeInvest.layer2.strong'),
    'above_average': t('safeInvest.layer2.aboveAverage'),
    'below_average': t('safeInvest.layer2.belowAverage'),
    'weak': t('safeInvest.layer2.weak'),
    'very_weak': t('safeInvest.layer2.veryWeak')
  }[rating] || rating);

  const getTrendLabel = (v) => v > 1.5 ? t('safeInvest.layer2.upStrong') : v > 0.5 ? t('safeInvest.layer2.up') : v > -0.5 ? t('safeInvest.layer2.sideways') : v > -1.5 ? t('safeInvest.layer2.down') : t('safeInvest.layer2.downStrong');
  const getFlowLabel = (v) => v > 1.5 ? t('safeInvest.layer2.inflowStrong') : v > 0.5 ? t('safeInvest.layer2.inflow') : v > -0.5 ? t('safeInvest.layer2.neutral') : v > -1.5 ? t('safeInvest.layer2.outflow') : t('safeInvest.layer2.outflowStrong');

  if (loading) return <SafeInvestSkeleton />;

  const buyNowStocks = stocksData?.stocks?.filter(s => s.signal === 'BUY_NOW') || [];
  const waitStocks = stocksData?.stocks?.filter(s => s.signal === 'WAIT_PULLBACK') || [];
  const buyDipStocks = stocksData?.stocks?.filter(s => s.signal === 'BUY_DIP') || [];
  const portfolioRisk = buyNowStocks.length > 0
    ? buyNowStocks.reduce((sum, s) => sum + (s.weight * (100 - s.score)), 0) / buyNowStocks.reduce((sum, s) => sum + s.weight, 0)
    : 0;

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      {/* ═══════ HEADER ═══════ */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.25)',
          }}>
            <ShieldIcon sx={{ color: 'white', fontSize: 22 }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
                {t('safeInvest.title')}
              </Typography>
              {regimeData?.regime && (
                <Chip
                  label={regimeData.regime.replace('_', ' ')}
                  size="small"
                  sx={{
                    height: 20, fontSize: '0.62rem', fontWeight: 700,
                    bgcolor: regimeData.regime === 'RISK_ON' ? '#dcfce7' : regimeData.regime === 'RECOVERY' ? '#dbeafe' : regimeData.regime === 'RISK_OFF' ? '#fee2e2' : '#fef3c7',
                    color: regimeData.regime === 'RISK_ON' ? '#16a34a' : regimeData.regime === 'RECOVERY' ? '#2563eb' : regimeData.regime === 'RISK_OFF' ? '#dc2626' : '#d97706',
                  }}
                />
              )}
            </Box>
            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('safeInvest.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
          onClick={handleRefresh}
          disabled={loading}
          sx={{ fontSize: '0.75rem', fontWeight: 600, minHeight: 32, textTransform: 'none' }}
        >
          Refresh
        </Button>
      </Box>

      {/* ═══════ LAYER 1: MARKET REGIME ═══════ */}
      <MarketRegimeCard regimeData={regimeData} interpretation={interpretation} />

      {/* ═══════ LAYER 2: SECTOR ROTATION ═══════ */}
      <SectorRotationLayer
        sectorsData={sectorsData}
        getRatingLabel={getRatingLabel}
        getTrendLabel={getTrendLabel}
        getFlowLabel={getFlowLabel}
      />

      {/* ═══════ LAYER 3: STOCK CANDIDATES ═══════ */}
      <StockCandidatesLayer
        stocksData={stocksData}
        buyNowStocks={buyNowStocks}
        waitStocks={waitStocks}
        buyDipStocks={buyDipStocks}
        portfolioRisk={portfolioRisk}
        sectorsData={sectorsData}
      />

      {/* ═══════ SNACKBAR ═══════ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SafeInvest;
